import { useEffect, useState, useCallback } from 'react';
import {
    LeaveType,
    fetchLeaveTypes,
    createLeaveType as apiCreateLeaveType,
    updateLeaveType as apiUpdateLeaveType,
    deleteLeaveType as apiDeleteLeaveType,
} from "../api/LeaveTypeApi";
import {ApiError} from "../api/types";


// Type for creating a new leave type (without generated fields)
export type CreateLeaveTypeData = Omit<LeaveType, 'id' | 'createdAt' | 'updatedAt'>;

// Type for updating an existing leave type (all fields optional except id)
export type UpdateLeaveTypeData = Partial<CreateLeaveTypeData>;

interface UseLeaveTypesReturn {
    leaveTypes: LeaveType[];
    loading: boolean;
    error: string | null;

    // Action states
    creating: boolean;
    updating: boolean;
    deleting: boolean;

    // Actions
    create: (leaveTypeData: CreateLeaveTypeData) => Promise<LeaveType | null>;
    update: (id: number, leaveTypeData: UpdateLeaveTypeData) => Promise<LeaveType | null>;
    remove: (id: number) => Promise<void>;
    refresh: () => Promise<void>;
}

export function useLeaveTypes(): UseLeaveTypesReturn {

    const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Individual action states
    const [creating, setCreating] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        loadLeaveTypes();
    }, []);

    const loadLeaveTypes = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetchLeaveTypes();
            setLeaveTypes(response.data || []);
        } catch (error) {
            console.error('Error loading leave types:', error);
            setError('Failed to load leave types');
            setLeaveTypes([]);
        } finally {
            setLoading(false);
        }
    };

    const refresh = useCallback(async () => {
        await loadLeaveTypes();
    }, []);

    // Private: Add leave type to list
    const addLeaveType = useCallback((leaveType: LeaveType) => {
        setLeaveTypes(prevLeaveTypes => [leaveType, ...prevLeaveTypes]);
    }, []);

    // Private: Remove leave type from list
    const removeLeaveType = useCallback((id: number) => {
        setLeaveTypes(prevLeaveTypes =>
            prevLeaveTypes.filter(lt => lt.id !== id)
        );
    }, []);

    // Private: Update leave type in list
    const updateLeaveTypeInList = useCallback((id: number, updates: Partial<LeaveType>) => {
        setLeaveTypes(prevLeaveTypes =>
            prevLeaveTypes.map(lt =>
                lt.id === id ? { ...lt, ...updates } : lt
            )
        );
    }, []);

    // Public: Create leave type (API call + add to list)
    const create = useCallback(async (leaveTypeData: CreateLeaveTypeData): Promise<LeaveType | null> => {
        setCreating(true);
        try {
            const response = await apiCreateLeaveType(leaveTypeData);

            if (response.success && response.data) {
                addLeaveType(response.data);  // Internal update
                return response.data;
            } else {
                throw new ApiError(response.error, 'Failed to create leave type');
            }
        } catch (err) {
            console.error('Error creating leave type:', err);
            throw err;
        } finally {
            setCreating(false);
        }
    }, [addLeaveType]);

    // Public: Update leave type (API call + update in list)
    const update = useCallback(async (id: number, leaveTypeData: UpdateLeaveTypeData): Promise<LeaveType | null> => {
        setUpdating(true);
        try {
            const response = await apiUpdateLeaveType(id, leaveTypeData);

            if (response.success && response.data) {
                updateLeaveTypeInList(id, response.data);  // Internal update
                return response.data;
            } else {
                throw new ApiError(response.error, 'Failed to update leave type');
            }
        } catch (err) {
            console.error('Error updating leave type:', err);
            throw err;
        } finally {
            setUpdating(false);
        }
    }, [updateLeaveTypeInList]);

    // Public: Delete leave type (API call + remove from list)
    const remove = useCallback(async (id: number) => {
        setDeleting(true);
        try {
            const response = await apiDeleteLeaveType(id);

            if (response.success) {
                removeLeaveType(id);  // Internal update
            } else {
                throw new ApiError(response.error, 'Failed to delete leave type');
            }
        } catch (err) {
            console.error('Error deleting leave type:', err);
            throw err;
        } finally {
            setDeleting(false);
        }
    }, [removeLeaveType]);

    return {
        leaveTypes,
        loading,
        error,
        creating,
        updating,
        deleting,
        create,
        update,
        remove,
        refresh
    };
}

export default useLeaveTypes;